import React, { useCallback } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { WarningCircle } from '@phosphor-icons/react';
import { useInputId } from './hooks';

const FormInputNumber = React.forwardRef(
  (
    {
      id: defaultId,
      icon: Icon,
      label,
      name,
      onChange,
      onSetFieldValue,
      value,
      error,
      required,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [id] = useInputId(defaultId);
    const handleKeyPress = useCallback((e) => {
      if (
        (e.charCode >= 48 && e.charCode <= 57) ||
        e.charCode === 44 ||
        e.charCode === 46
      )
        return;
      e.preventDefault();
    }, []);
    const handleChange = ({ target }) => {
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: target.value,
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, target.value);
      }
    };
    const showIcon = Icon || error;
    const isError = !!error;
    return (
      <div className="space-y-2">
        {label && (
          <label
            htmlFor={id}
            className="block text-xs font-semibold text-neutral-700"
          >
            <span>{label}</span>
            {required && (
              <span className="ml-1 font-light text-danger-500">*</span>
            )}
          </label>
        )}
        <div
          className={cn(
            'relative border rounded px-3 py-2 focus-within:ring-1 z-[1]',
            isError
              ? 'border-danger-500 focus-within:ring-danger-500 focus-within:border-danger-500 animate-wiggle'
              : 'border-neutral-300 focus-within:ring-brand-700 focus-within:border-brand-700',
            disabled ? 'bg-neutral-200' : 'bg-white',
          )}
        >
          <input
            id={id}
            ref={ref}
            type="text"
            className={cn(
              'block w-full border-0 p-0 placeholder-hidden focus:ring-0 text-base focus:outline-none',
              disabled
                ? 'bg-neutral-200 text-neutral-500'
                : 'bg-white text-neutral-900',
              showIcon ? 'pr-10' : '',
            )}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            value={value || ''}
            disabled={disabled}
            {...props}
          />
          {showIcon && (
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              title={error || ''}
            >
              {error ? (
                <WarningCircle
                  className="h-5 w-5 text-danger-500"
                  aria-hidden="true"
                />
              ) : (
                <Icon className="h-5 w-5 text-neutral-500" aria-hidden="true" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

FormInputNumber.defaultProps = {
  id: '',
  icon: false,
  type: 'text',
  error: null,
  required: false,
  onChange: false,
  onSetFieldValue: false,
  disabled: false,
};

FormInputNumber.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  disabled: PropTypes.bool,
};

export default React.memo(FormInputNumber);
